import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowUpRight } from "lucide-react";

const FALLBACK_IMAGES = {
  cement: "/assets/categories/cement.jpg",
  steel: "/assets/categories/steel.jpg",
  structuralsteel: "/assets/categories/Structural_Steel.jpg"
};

function normalizeProductSlug(slug) {
  const value = String(slug || "").trim();
  if (value === "di-pipes") return "pvc-pipes";
  if (value.toLowerCase() === "acc-block") return "Acc-block";
  return value;
}

function routeSlugFromProductSlug(slug) {
  const value = normalizeProductSlug(slug);
  if (value === "pvc-pipes") return "di-pipes";
  return value;
}

export default function CategoriesGrid() {
  const [products, setProducts] = useState([]);
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (loading) return;

    if (user?.role === "vendor" && !user.vendor_product_slug) {
      setProducts([]);
      return;
    }

    if (user?.role === "vendor") {
      setProducts([]);
    }

    let alive = true;

    api.get("/products")
      .then(({ data }) => {
        if (!alive) return;
        const sorted = [...data].sort((a, b) => (a.order || 0) - (b.order || 0));
        if (user?.role === "vendor" && user.vendor_product_slug) {
          const slug = normalizeProductSlug(user.vendor_product_slug);
          const vendorBrand = String(user.vendor_brand || "").trim();
          setProducts(sorted
            .filter((p) => normalizeProductSlug(p.slug) === slug)
            .map((p) => (vendorBrand ? { ...p, brands: [vendorBrand] } : p)));
        } else {
          setProducts(sorted);
        }
      })
      .catch(() => {
        if (alive) setProducts([]);
      });

    return () => { alive = false; };
  }, [loading, user?.role, user?.vendor_product_slug, user?.vendor_brand]);

  return (
    <section id="categories" className="py-20 md:py-28 px-6 md:px-10 bg-white" data-testid="categories-section">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <div className="section-label">Product Catalogue</div>
            <h2 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl text-[#111827] mt-2 tracking-tight">
              Browse by Category
            </h2>
            <p className="mt-3 text-[#4B5563] text-base max-w-lg">
              Verified product lines · Transparent pricing · Pan-India delivery
            </p>
          </div>
          <div className="text-sm text-[#4B5563]">
            Showing <span className="font-bold text-[#111827]">{products.length}</span> categories
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6" data-testid="categories-grid">
          {products.map((p) => (
            <button
              key={p.slug}
              onClick={() => navigate(`/products/${routeSlugFromProductSlug(p.slug)}`)}
              className="category-card bg-white border border-gray-200 rounded-xl p-4 text-left group"
              data-testid={`category-card-${p.slug}`}
            >
              <div className="aspect-square rounded-md overflow-hidden bg-gray-100 mb-4 relative">
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  onError={(e) => {
                    const fallback = FALLBACK_IMAGES[p.slug];
                    if (fallback && e.currentTarget.src !== fallback) e.currentTarget.src = fallback;
                  }}
                />
                <div className="absolute top-2 right-2 bg-white/90 backdrop-blur rounded-full w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowUpRight className="w-4 h-4 text-[#C8A96A]" />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <h3 className="font-display font-semibold text-base text-[#111827] group-hover:text-[#C8A96A] transition-colors">
                  {p.name}
                </h3>
              </div>
              <p className="text-xs text-[#4B5563] mt-1">
                {user?.role === "vendor" && user.vendor_brand
                  ? user.vendor_brand
                  : p.brands?.slice(0, 3).join(" · ")}
              </p>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
